import {
  ACTION_STATUS,
  AVAILABLE_ACTIONS,
  FEATURES,
  IActionOnReminderData,
  REMINDER_ACTIONS,
  REMINDER_TYPE,
} from "@home-health-notify/domain";
import { Box, Typography } from "@mui/material";
import {
  formatRescheduleDateTime,
  formatSchedulerDate,
  formatTime,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";
import { IMessageContentTypeBubble } from "../../MessagePanel";
import {
  ReferralInviteActionsStyle,
  ReferralInviteActionsWrapper,
  RemindAndRevokeBtn,
  ReminderHeading,
  ReminderSuggestion,
} from "../ReminderContent.style";
import dayjs from "dayjs";
import { UserFirstName } from "../../../common/user/UserText";
import ExpandableText from "../../../common/ExpandableText";

const ReminderContent = ({ message }: IMessageContentTypeBubble) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  let messageData = message.getMessage();
  const { reminder, from } = messageData;
  const isReferralInvite =
    messageData.reminder?.reminderType === REMINDER_TYPE.SYS_REFERRAL_REMINDER;
  const isReferralActive =
    messageData.reminder?.action?.actionStatus === ACTION_STATUS.ACTIVE;
  const remindAgainIn =
    messageData.reminder?.action?.actionExecuted ===
    AVAILABLE_ACTIONS.REMIND_AGAIN;
  const referredUser = messageData.reminder?.title;
  const referralRescheduleData =
    messageData.reminder?.action?.actionMeta?.rescheduleTime;
  const referralRescheduleDataFormatted =
    typeof referralRescheduleData === "number"
      ? formatRescheduleDateTime(referralRescheduleData)
      : "";
  const schedulerDate = formatSchedulerDate(reminder?.schedulerDate ?? 0);
  const schedulerTime = formatTime(reminder?.schedulerTime ?? 0);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const reminderOptions = [
    {
      title: tNs("referral.reminderOptions.fifteenMin"),
      action: REMINDER_ACTIONS.REMIND_AGAIN,
      value: 0.25,
    },
    {
      title: tNs("referral.reminderOptions.oneHour"),
      action: REMINDER_ACTIONS.REMIND_AGAIN,
      value: 1,
    },
    {
      title: tNs("referral.reminderOptions.eightHours"),
      action: REMINDER_ACTIONS.REMIND_AGAIN,
      value: 8,
    },
    {
      title: tNs("referral.reminderOptions.twentyFourHours"),
      action: REMINDER_ACTIONS.REMIND_AGAIN,
      value: 24,
    },
    {
      title: tNs("referral.reminderOptions.revokeReferral"),
      action: REMINDER_ACTIONS.REVOKE,
      value: undefined,
    },
  ];

  const onReminderClick = (selectedOption: {
    title: string;
    action: REMINDER_ACTIONS;
    value?: number;
  }) => {
    const reminderData: IActionOnReminderData = {
      action: selectedOption.action,
    };
    if (
      selectedOption.action === REMINDER_ACTIONS.REMIND_AGAIN &&
      selectedOption.value
    ) {
      reminderData.scheduleTime = dayjs()
        .add(selectedOption.value, "hour")
        .valueOf();
    }
    message.actionOnReferralReminder(reminderData);
  };

  return (
    <Box>
      <ReminderHeading variant="body2" data-testid="reminder-heading">
        <>
          {isReferralInvite ? (
            <>
              <b>{referredUser} </b>{" "}
              <ExpandableText>
                {tNs("referral.referralInviteText")}
              </ExpandableText>
            </>
          ) : (
            <ExpandableText>
              <UserFirstName userId={from} />
              {` ${tNs("referral.setReminderFor")} ${
                reminder?.title
              } - ${schedulerDate}, ${schedulerTime}`}
            </ExpandableText>
          )}
        </>
      </ReminderHeading>
      <Typography variant="body2" data-testid="reminder-message">
        <ExpandableText>
          {reminder?.message}{" "}
          {!isReferralActive &&
            remindAgainIn &&
            referralRescheduleDataFormatted}{" "}
        </ExpandableText>
      </Typography>
      {selectedOrgUser?.hasAccess(FEATURES.SEND_REFERRALS) &&
        isReferralInvite &&
        isReferralActive && (
          <>
            <ReminderSuggestion data-testid="reminder-suggestion">
              {tNs("referral.referralResponseSuggestion")}
            </ReminderSuggestion>
            <ReferralInviteActionsStyle>
              <ReferralInviteActionsWrapper data-testid="referral-reminder-options">
                {reminderOptions.map((option, index) => (
                  <RemindAndRevokeBtn
                    variant="outlined"
                    color={index === 4 ? "warning" : "inherit"}
                    size="small"
                    key={index}
                    onClick={() => onReminderClick(option)}
                  >
                    {option.title}
                  </RemindAndRevokeBtn>
                ))}
              </ReferralInviteActionsWrapper>
            </ReferralInviteActionsStyle>
          </>
        )}
    </Box>
  );
};

export default ReminderContent;
